import React, { useEffect, useRef } from 'react';
import { useCurrentAgreement } from '@economic/react-app';
import { HelpDrawerView } from '~/app/components/Header/Help/HelpDrawerView';
import { useSessionStorage } from '~/app/components/Header/Help/ai-chat/useSessionStorage';
import * as inapphelpMixpanel from '~/app/components/Header/Help/tracking';
import { ApplaiData } from './types';

const APPLAI_WORKSPACE_ID = '4L8V4bRXR1e2LkPrYTAo';
const APPLAI_ORIGIN = 'https://chat.applai.io';

export interface ApplaiViewProps {
    title: string | React.JSX.Element;
    initialQuestion?: string;
}

export function ApplaiView({ title, initialQuestion }: ApplaiViewProps): React.JSX.Element {
    const applaiRef = useRef<HTMLIFrameElement>(null);
    const currentAgreement = useCurrentAgreement();
    const [conversationId, setConversationId] = useSessionStorage(
        `agreement.${currentAgreement.number}.${initialQuestion ? 'applaiSessionSearch' : 'applaiSessionChat'}`,
        ''
    );

    useEffect(() => {
        function handleMessage(e: MessageEvent<ApplaiData>) {
            if (e.origin === APPLAI_ORIGIN) {
                switch (e.data.type) {
                    case 'init':
                        inapphelpMixpanel.trackAiChatEvents({ event: e.data.type, data: {} });

                        if (conversationId) {
                            applaiRef.current?.contentWindow?.postMessage(
                                {
                                    type: 'init',
                                    workspaceId: APPLAI_WORKSPACE_ID,
                                    conversationId,
                                },
                                APPLAI_ORIGIN
                            );
                        }

                        if (initialQuestion) {
                            setTimeout(
                                () => {
                                    applaiRef.current?.contentWindow?.postMessage(
                                        { type: 'ask', question: initialQuestion },
                                        APPLAI_ORIGIN
                                    );
                                },
                                conversationId ? 100 : 0
                            );
                        }

                        break;
                    case 'conversation-ask':
                        inapphelpMixpanel.trackAiChatEvents({ event: e.data.type, data: {} });
                        break;
                    case 'conversation-started':
                        inapphelpMixpanel.trackAiChatEvents({
                            event: e.data.type,
                            data: { suggested: !!e.data.data.meta?.isSuggested },
                        });
                        setConversationId(e.data.data.conversationId);
                        break;
                    case 'conversation-answer':
                        inapphelpMixpanel.trackAiChatEvents({ event: e.data.type, data: { validated: e.data.data.validated } });
                        break;
                    case 'message-action':
                        inapphelpMixpanel.trackAiChatEvents({ event: e.data.type, data: { type: e.data.data.type } });
                        break;
                    case 'source-clicked':
                        inapphelpMixpanel.trackAiChatEvents({ event: e.data.type, data: {} });
                        const url = new URL(e.data.url);
                        window.open(url, '_blank');
                        break;
                    default:
                        break;
                }
            }
        }

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [conversationId, initialQuestion, setConversationId]);

    return (
        <HelpDrawerView title={title}>
            <iframe
                ref={applaiRef}
                title="AI chat"
                className="grow flex"
                src={`https://chat.applai.io/embed?workspaceId=${APPLAI_WORKSPACE_ID}&isClosable=false&alignment=top`}
            />
        </HelpDrawerView>
    );
}
